/**
 * GESKE UK: PDP hero ripple background.
 *
 * A 1:1 port of geske.com's ProductHero `Background` WebGL shader
 * (src/client-layer/redesign-three/features/product/ProductHero/components/Background)
 * rendered into a lightweight, dependency-free <canvas> (no Three.js). It draws
 * a soft vertical warm gradient with concentric shadow rings that emanate from a
 * point behind the device, spaced non-linearly (d^2) so they bunch toward the
 * edges, with the centre and vertical mid-line masked clean.
 *
 * Mounts on [data-product-ripple] inside .product-main__gallery-bg. The element
 * keeps a plain CSS gradient (same colours) as the no-WebGL fallback, so if a
 * context can't be created we simply leave that gradient in place. Styling lives
 * in assets/geske-product.css.
 */
(function () {
  'use strict';

  var VERT = [
    'attribute vec2 position;',
    'varying vec2 vUv;',
    'void main() {',
    '  vUv = position * 0.5 + 0.5;', // clip-space quad -> 0..1 uv, y up (0 bottom)
    '  gl_Position = vec4(position, 0.0, 1.0);',
    '}'
  ].join('\n');

  // Ported verbatim from the .com Background fragment shader. Three's output
  // sRGB conversion nets back to ~the source hex (verified by sampling the live
  // canvas), so we feed the raw grey colours and output directly with no gamma.
  var FRAG = [
    'precision highp float;',
    'uniform vec2 uRez;',
    'uniform float uTime;',
    'uniform vec3 uColorTop;',
    'uniform vec3 uColorBottom;',
    'uniform float uOffsetLeft;',
    'uniform float uOffsetTop;',
    'varying vec2 vUv;',
    'const float SHADOW_DARKNESS = 0.1;',
    'const float RIPPLE_TIME = 1.0;',
    'const float RIPPLES_INVERTED_COUNT = 1.1;', // the lower, the more ripples
    'const float RIPPLE_INVERTED_RADIUS = 2.0;', // the lower, the bigger radius
    'const float SCALE = 1.0;',                   // the lower, the bigger
    'void main() {',
    '  vec2 uv = vUv;',
    '  vec3 colBg = mix(uColorBottom, uColorTop, vUv.y);',
    '  vec3 col = colBg;',
    '  uv -= 0.5;',                       // recenter to screen middle
    '  uv.x *= uRez.x / uRez.y;',         // apply aspect ratio
    '  uv.x += uOffsetLeft;',             // shift ripple origin horizontally
    '  uv.y += uOffsetTop;',              // shift ripple origin vertically
    '  uv *= SCALE;',
    '  uv += 0.5;',
    '  float d = length(uv - 0.5) * RIPPLE_INVERTED_RADIUS;',
    '  float t = d * d * 10.0 - uTime * RIPPLE_TIME;',
    '  float shadowEdge = clamp(1.0 - mod(t * 0.5, RIPPLES_INVERTED_COUNT), 0.0, 1.0);',
    '  d = clamp(shadowEdge * (1.0 - d), 0.0, 1.0);',
    '  vec3 ripples = col - clamp(d * SHADOW_DARKNESS, 0.0, 0.1);',
    '  float firstCircle = 1.0 - pow(length(uv - 0.5) * 2.5, 3.0);', // clean zone around device
    '  col = mix(ripples, colBg, firstCircle);',
    '  float noShadowAroundMiddleX = (1.0 - abs(uv.x - 0.5) * 5.0);',
    '  col = mix(col, colBg, noShadowAroundMiddleX);',
    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  // The ripple origin is anchored dynamically to the centre of the active hero
  // media (see computeOffsets in initCanvas) rather than a fixed per-breakpoint
  // offset, so the rings stay locked to the device centre at any viewport size
  // and re-anchor when the shopper switches media.

  function hexToRgb(hex, fallback) {
    if (!hex) return fallback;
    var h = hex.trim().replace('#', '');
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    if (h.length !== 6) return fallback;
    var n = parseInt(h, 16);
    if (isNaN(n)) return fallback;
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  function initCanvas(canvas) {
    if (canvas.dataset.rippleInit === 'true') return;
    canvas.dataset.rippleInit = 'true';

    var gl = canvas.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false }) ||
      canvas.getContext('experimental-webgl', { antialias: false, alpha: false });
    if (!gl) return; // CSS gradient fallback stays in place.

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    var program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    var buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    // Two triangles (strip) covering clip space.
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1, 1, -1, -1, 1, 1, 1
    ]), gl.STATIC_DRAW);
    var posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    var u = {
      rez: gl.getUniformLocation(program, 'uRez'),
      time: gl.getUniformLocation(program, 'uTime'),
      top: gl.getUniformLocation(program, 'uColorTop'),
      bottom: gl.getUniformLocation(program, 'uColorBottom'),
      offsetLeft: gl.getUniformLocation(program, 'uOffsetLeft'),
      offsetTop: gl.getUniformLocation(program, 'uOffsetTop')
    };

    var top = hexToRgb(canvas.dataset.colorTop, [0.322, 0.247, 0.149]);    // #523f26
    var bottom = hexToRgb(canvas.dataset.colorBottom, [0.596, 0.502, 0.361]); // #98805c
    gl.uniform3fv(u.top, top);
    gl.uniform3fv(u.bottom, bottom);

    // --- Anchor the ring centre to the centre of the active hero media. The
    // shader places the ripple origin (where the rings emanate from) at:
    //   centre.x (0..1 from left) = 0.5 - uOffsetLeft / aspect
    //   centre.y (0..1 from top)  = 0.5 + uOffsetTop
    // so we invert those from the device's measured position each layout. This
    // keeps the origin locked to the device at any viewport size, and re-anchors
    // to the active photo on non-3D PDPs or when the shopper switches media.
    var section = canvas.closest ? canvas.closest('.product-main') : null;

    function findTarget() {
      var item = section && section.querySelector('.product-gallery__item.is-active');
      if (item) {
        return item.querySelector('model-viewer, .product-gallery__model, img, video') || item;
      }
      return section && section.querySelector('.product-main__media');
    }

    function computeOffsets() {
      var target = findTarget();
      if (!target) return; // keep last-known offsets
      var cr = canvas.getBoundingClientRect();
      var tr = target.getBoundingClientRect();
      if (!cr.width || !cr.height || !tr.width || !tr.height) return;
      var cx = (tr.left + tr.width / 2 - cr.left) / cr.width;  // 0..1 from left
      var cy = (tr.top + tr.height / 2 - cr.top) / cr.height;  // 0..1 from top
      // Clamp so media that is partly off-canvas can't throw the origin far out.
      cx = Math.min(1, Math.max(0, cx));
      cy = Math.min(1, Math.max(0, cy));
      gl.uniform1f(u.offsetLeft, (cr.width / cr.height) * (0.5 - cx));
      gl.uniform1f(u.offsetTop, cy - 0.5);
    }

    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var start = null;
    var running = false;
    var visible = true;
    var painted = false;

    function draw(time) {
      gl.uniform1f(u.time, time);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (!painted) {
        painted = true;
        canvas.classList.add('is-ready');
      }
    }

    // Size the drawing buffer and re-anchor the origin. Kept out of the frame
    // loop (it reads layout) and run only on the events below.
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    function relayout() {
      var w = Math.max(1, canvas.clientWidth);
      var h = Math.max(1, canvas.clientHeight);
      var pw = Math.round(w * dpr);
      var ph = Math.round(h * dpr);
      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
        gl.viewport(0, 0, pw, ph);
      }
      gl.uniform2f(u.rez, pw, ph);
      computeOffsets();
      if (reduceMotion) draw(0); // keep the frozen frame anchored after relayout
    }

    function frame(now) {
      if (!running) return;
      if (start === null) start = now;
      draw((now - start) / 1000);
      requestAnimationFrame(frame);
    }

    function play() {
      if (running || reduceMotion) return;
      running = true;
      requestAnimationFrame(frame);
    }
    function pause() {
      running = false;
      start = null;
    }

    // Coalesce re-anchors into the next frame so a burst of events (resize +
    // mutation + load) does a single layout read.
    var relayoutScheduled = false;
    function scheduleRelayout() {
      if (relayoutScheduled) return;
      relayoutScheduled = true;
      requestAnimationFrame(function () {
        relayoutScheduled = false;
        relayout();
      });
    }

    // Initial size + anchor before the first paint so there is no visible jump.
    relayout();

    window.addEventListener('resize', scheduleRelayout);
    window.addEventListener('load', scheduleRelayout);

    // Catch container resizes (column reflow, breakpoint changes) without a
    // window resize, plus the media box growing as it loads.
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(scheduleRelayout);
      ro.observe(canvas);
      var mediaWrap = section && section.querySelector('.product-main__media');
      if (mediaWrap) ro.observe(mediaWrap);
    }

    // A thumbnail click toggles .is-active on the gallery items; re-anchor to
    // the newly active media. Also re-anchor once the model loads and reframes.
    if ('MutationObserver' in window && section) {
      var gallery = section.querySelector('[data-gallery]') || section;
      var mo = new MutationObserver(scheduleRelayout);
      mo.observe(gallery, { subtree: true, attributes: true, attributeFilter: ['class'] });
      var mv = section.querySelector('model-viewer');
      if (mv) mv.addEventListener('load', scheduleRelayout);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        pause();
      } else if (visible) {
        if (reduceMotion) relayout();
        else play();
      }
    });

    // Only animate while the hero is on screen.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible = entry.isIntersecting;
          if (visible) {
            if (reduceMotion) relayout();
            else play();
          } else {
            pause();
          }
        });
      }, { threshold: 0 });
      io.observe(canvas);
    } else {
      if (reduceMotion) relayout();
      else play();
    }
  }

  function init() {
    var canvases = document.querySelectorAll('[data-product-ripple]');
    for (var i = 0; i < canvases.length; i++) {
      initCanvas(canvases[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
